"use server";

import { AuthError } from "next-auth";
import { redirect } from "next/navigation";
import { getLocale, getTranslations } from "next-intl/server";

import { signIn } from "@/auth";
import { checkBot, formIssuedAt, FORM_TOKEN_FIELD, issueFormToken } from "@/lib/auth/bot-check";
import { allow, LIMITS } from "@/lib/auth/rate-limit";
import { directoryOwned, MAX_COMPANY_NAME, MAX_NAME, normalizeEmail } from "@/lib/directory";
import { DEFAULT_LOCALE, isLocale } from "@/lib/i18n/config";
import {
  confirmRegistration,
  PASSWORD_MAX,
  PASSWORD_MIN,
  REGISTRATION_CONFIRM_TTL_HOURS,
  registerPending,
  registerWithInvite,
  type ConfirmOutcome,
} from "@/lib/registration";

export interface RegisterState {
  error?: string;
  /** A plain registration went through: show "check your inbox". */
  done?: boolean;
  /** The invite stopped working meanwhile: the form falls back to a plain sign-up. */
  dropInvite?: boolean;
  /** What was typed, so a failed submit doesn't clear the form (never the password). */
  values?: { name: string; email: string; companyName: string; partnerName: string };
  nonce?: number;
  /** A fresh bot-check token (lib/auth/bot-check.ts) for the next submit. */
  formToken?: string;
}

const FORM = "register";

// Signed out by design (proxy.ts leaves /register open). Every check runs
// here; lib/registration.ts does the writes in one transaction each.
export async function registerAction(_prev: RegisterState, formData: FormData): Promise<RegisterState> {
  const t = await getTranslations("register");
  const name = String(formData.get("name") ?? "").trim();
  const rawEmail = String(formData.get("email") ?? "");
  const companyName = String(formData.get("companyName") ?? "").trim();
  // Optional (migration 056): a company already on the platform the registrant
  // works with; its leads may then approve the registration. Plain sign-ups only.
  const partnerName = String(formData.get("partnerName") ?? "").trim().replace(/\s+/g, " ");
  const password = String(formData.get("password") ?? "");
  const confirm = String(formData.get("confirmPassword") ?? "");
  const token = String(formData.get("invite") ?? "");
  const values = { name, email: rawEmail.trim(), companyName, partnerName };
  // The retry token keeps the first render time, so fixing a typo isn't held
  // back by the minimum fill time. An invalid one starts the clock now.
  const issuedAt = formIssuedAt(FORM, String(formData.get(FORM_TOKEN_FIELD) ?? "")) ?? Date.now();
  const fail = (error: string, extra: Partial<RegisterState> = {}): RegisterState => ({
    error,
    values,
    nonce: Date.now(),
    formToken: issueFormToken(FORM, issuedAt),
    ...extra,
  });

  if (!(await directoryOwned())) return fail(t("closed"));
  if (!name || name.length > MAX_NAME) return fail(t("errors.name"));
  if (!companyName || companyName.length > MAX_COMPANY_NAME) return fail(t("errors.companyName"));
  if (partnerName.length > MAX_COMPANY_NAME) return fail(t("errors.partnerName"));
  const email = token ? null : normalizeEmail(rawEmail);
  if (!token && !email) return fail(t("errors.email"));
  if (password.length < PASSWORD_MIN) return fail(t("errors.passwordTooShort", { min: PASSWORD_MIN }));
  if (password.length > PASSWORD_MAX) return fail(t("errors.passwordTooLong"));
  if (password !== confirm) return fail(t("errors.passwordMismatch"));
  if (!(await allow(["register:{ip}", LIMITS.registerPerIp]))) return fail(t("errors.rateLimited"));

  // Bot checks (lib/auth/bot-check.ts) after the rate limit, so a bot retrying
  // the captcha still burns its per-IP budget. A filled honeypot gets the same
  // "received" answer as a real sign-up, and nothing is written.
  const bot = await checkBot(FORM, formData);
  if (bot === "honeypot") return { done: true, nonce: Date.now() };
  if (bot === "tooFast") return fail(t("errors.tooFast"));
  if (bot === "expired") return fail(t("errors.formExpired"));
  if (bot === "captchaFailed") return fail(t("errors.captchaFailed"));
  if (bot === "captchaUnavailable") return fail(t("errors.captchaUnavailable"));

  const current = await getLocale();
  const locale = isLocale(current) ? current : DEFAULT_LOCALE;

  if (token) {
    const result = await registerWithInvite(token, { name, companyName, password, locale });
    if (!result.ok) {
      return result.error === "invite_invalid"
        ? fail(t("errors.inviteNoLongerValid"), { dropInvite: true })
        : fail(t("errors.emailTaken"));
    }
    // Approved and active already: sign straight in with what they just chose.
    try {
      await signIn("password", { email: result.email, password, redirect: false });
    } catch (err) {
      if (!(err instanceof AuthError)) throw err;
      redirect("/login?registered=1");
    }
    redirect("/");
  }

  const result = await registerPending({
    name,
    email: email!,
    companyName,
    password,
    locale,
    partnerName: partnerName || null,
  });
  if (!result.ok) {
    if (result.error === "awaiting_confirmation") {
      return fail(t("errors.awaitingConfirmation", { hours: REGISTRATION_CONFIRM_TTL_HOURS }));
    }
    if (result.error === "email_failed") return fail(t("errors.emailFailed"));
    return fail(t("errors.emailTaken"));
  }
  return { done: true, nonce: Date.now() };
}

export interface ConfirmState {
  outcome?: ConfirmOutcome;
}

// The emailed /register/confirm?token=… link (migration 055). A button posts
// here rather than the page confirming on load, so a mail scanner that fetches
// the link doesn't confirm on the registrant's behalf.
export async function confirmRegistrationAction(_prev: ConfirmState, formData: FormData): Promise<ConfirmState> {
  const token = String(formData.get("token") ?? "");
  return { outcome: await confirmRegistration(token) };
}
